@let e = execution();
<header class="aud-head">
  @if (e) {
    <span class="aud-badge aud-badge--{{ e.status }}">
      <i class="fa-solid" [class]="e.status === 'R' ? 'fa-circle-notch fa-spin' : e.status === 'S' ? 'fa-check' : e.status === 'F' ? 'fa-xmark' : 'fa-ban'" aria-hidden="true"></i>
      {{ statusKey(e.status) | translate }}
    </span>
    <h2 class="aud-title" [title]="e.processValue">
      <i class="fa-solid" [class]="e.type === 'R' ? 'fa-file-lines' : 'fa-gears'" aria-hidden="true"></i>
      {{ e.processName }}
    </h2>
  }
  <button type="button" class="aud-icon-btn aud-close" (click)="closed.emit()" [attr.aria-label]="'audit.actions.close' | translate">
    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
  </button>
</header>

@if (e) {
  <div class="aud-body">
    @if (stale(e)) {
      <p class="aud-note aud-note--warn">
        <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ 'audit.execution.stale' | translate }}
      </p>
    }
    @if (e.summary) {
      <section class="aud-summary aud-summary--{{ e.status }}">
        <h3>{{ (e.status === 'F' ? 'audit.execution.error' : 'audit.execution.result') | translate }}</h3>
        <p>{{ e.summary }}</p>
      </section>
    }

    <dl class="aud-meta">
      <div>
        <dt>{{ 'audit.execution.id' | translate }}</dt>
        <dd>
          <code>{{ e.id }}</code>
          <button type="button" class="aud-icon-btn" (click)="copyId(e)" [attr.aria-label]="'audit.actions.copy' | translate">
            <i class="fa-solid" [class]="copied() ? 'fa-check' : 'fa-copy'" aria-hidden="true"></i>
          </button>
        </dd>
      </div>
      <div>
        <dt>{{ 'audit.execution.type' | translate }}</dt>
        <dd>{{ (e.type === 'R' ? 'audit.type.R' : 'audit.type.P') | translate }}</dd>
      </div>
      <div>
        <dt>{{ 'audit.execution.started' | translate }}</dt>
        <dd [title]="fmt().exact(e.created)">{{ fmt().dateTime(e.created) }}</dd>
      </div>
      <div>
        <dt>{{ (e.status === 'R' ? 'audit.execution.lastUpdate' : 'audit.execution.ended') | translate }}</dt>
        <dd [title]="fmt().exact(e.updated)">{{ fmt().dateTime(e.updated) }}</dd>
      </div>
      <div>
        <dt>{{ 'audit.execution.duration' | translate }}</dt>
        <dd>
          {{ duration(e.durationMs) || '-' }}
          @if (detail()?.averageMs; as avg) {
            <small class="aud-muted">({{ 'audit.execution.average' | translate: { value: duration(avg) } }})</small>
          }
          @if (slower()) {
            <span class="aud-tag aud-tag--warn">{{ 'audit.execution.slower' | translate }}</span>
          }
        </dd>
      </div>
      <div>
        <dt>{{ 'audit.execution.user' | translate }}</dt>
        <dd>{{ e.userName || '-' }} @if (e.roleName) {<small class="aud-muted">{{ e.roleName }}</small>}</dd>
      </div>
      <div>
        <dt>{{ 'audit.execution.tenantOrg' | translate }}</dt>
        <dd>{{ e.clientName || '-' }} / {{ e.orgName || '-' }}</dd>
      </div>
      <div>
        <dt>{{ 'audit.execution.session' | translate }}</dt>
        <dd><code>{{ e.sessionId || '-' }}</code></dd>
      </div>
      @if (e.recordId > 0) {
        <div>
          <dt>{{ 'audit.execution.record' | translate }}</dt>
          <dd><code>{{ e.recordId }}</code></dd>
        </div>
      }
    </dl>

    @if (e.parentId > 0) {
      <p class="aud-note">
        <i class="fa-solid fa-diagram-project" aria-hidden="true"></i>{{ 'audit.execution.subRunOf' | translate }}
        <button type="button" class="aud-link-btn" (click)="openRun.emit(e.parentId)">#{{ e.parentId }}</button>
      </p>
    }
    @if (stopNote(); as note) {
      <p class="aud-note aud-note--warn" role="status">
        <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ note | translate }}
      </p>
    }
    @if (e.status === 'R' && canStop() && !stale(e)) {
      <div class="aud-stop" role="group" [attr.aria-label]="'audit.execution.stop' | translate">
        @if (stopping()) {
          <span class="aud-muted"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'audit.execution.stopping' | translate }}</span>
        } @else if (confirmStop()) {
          <span>{{ (e.parentId > 0 ? 'audit.execution.stopConfirmRoot' : 'audit.execution.stopConfirm') | translate }}</span>
          <button type="button" class="p-button p-button-alert aud-stop-btn" (click)="askStop(e)">
            <i class="fa-solid fa-stop" aria-hidden="true"></i>{{ 'audit.execution.stop' | translate }}
          </button>
          <button type="button" class="p-button p-button-secondary aud-stop-btn" (click)="confirmStop.set(false)">{{ 'audit.actions.cancel' | translate }}</button>
        } @else {
          <button type="button" class="p-button p-button-alert aud-stop-btn" (click)="askStop(e)">
            <i class="fa-solid fa-stop" aria-hidden="true"></i>{{ 'audit.execution.stop' | translate }}
          </button>
        }
      </div>
    }
    <div class="aud-links">
      @if (e.sessionId > 0) {
        <button type="button" class="aud-link-btn" (click)="showChanges.emit(e)">
          <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>{{ 'audit.execution.showChanges' | translate }}
        </button>
      }
      <button type="button" class="aud-link-btn" (click)="filterProcess.emit(e)">
        <i class="fa-solid fa-filter" aria-hidden="true"></i>{{ 'audit.execution.sameProcess' | translate }}
      </button>
      @if (e.userId > 0) {
        <button type="button" class="aud-link-btn" (click)="filterUser.emit(e)">
          <i class="fa-solid fa-user" aria-hidden="true"></i>{{ 'audit.execution.sameUser' | translate }}
        </button>
      }
    </div>

    @if (loading()) {
      <p class="aud-state"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'audit.state.loading' | translate }}</p>
    } @else if (error()) {
      <p class="aud-state aud-state--error">
        <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ error() }}
        <button type="button" class="aud-link-btn" (click)="retry.emit()">{{ 'audit.actions.retry' | translate }}</button>
      </p>
    } @else if (detail()) {
      @let d = detail()!;
      <section class="aud-section">
        <h3>{{ 'audit.execution.params' | translate }} <span class="aud-count">{{ d.params.length }}</span></h3>
        @if (d.params.length === 0) {
          <p class="aud-muted">{{ 'audit.execution.noParams' | translate }}</p>
        } @else {
          <table class="aud-kv">
            <tbody>
              @for (p of d.params; track p.name) {
                @let json = pretty(p.value);
                <tr>
                  <th scope="row" [title]="p.name">{{ p.label }}</th>
                  <td>
                    @if (p.masked) {
                      <span class="aud-masked"><i class="fa-solid fa-lock" aria-hidden="true"></i>{{ 'audit.masked' | translate }}</span>
                    } @else if (json) {
                      <pre class="aud-pre">{{ json }}</pre>
                    } @else {
                      <span class="aud-value">{{ p.value ?? '-' }}</span>
                      @if (p.valueTo) {
                        <span class="aud-muted"> - </span><span class="aud-value">{{ p.valueTo }}</span>
                      }
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </section>

      @if (d.children.length) {
        <section class="aud-section">
          <h3>{{ 'audit.execution.subRuns' | translate }} <span class="aud-count">{{ d.children.length }}</span></h3>
          <ul class="aud-files">
            @for (c of d.children; track c.id) {
              <li>
                <span class="aud-badge aud-badge--{{ c.status }}">{{ statusKey(c.status) | translate }}</span>
                <button type="button" class="aud-link-btn" (click)="openRun.emit(c.id)">{{ c.processName }} #{{ c.id }}</button>
                <small class="aud-muted">{{ fmt().dateTime(c.created) }}@if (duration(c.durationMs); as dur) { - {{ dur }}}</small>
              </li>
            }
          </ul>
        </section>
      }

      @if (d.files.length) {
        <section class="aud-section">
          <h3>{{ 'audit.execution.files' | translate }} <span class="aud-count">{{ d.files.length }}</span></h3>
          <ul class="aud-files">
            @for (f of d.files; track f.url) {
              <li>
                <i class="fa-solid fa-file-arrow-down" aria-hidden="true"></i>
                <button type="button" class="aud-link-btn" (click)="download(f.url)">{{ f.name || f.url }}</button>
              </li>
            }
          </ul>
        </section>
      }

      <section class="aud-section">
        <h3>{{ 'audit.execution.logs' | translate }} <span class="aud-count">{{ d.logs.length }}{{ d.logsTruncated ? '+' : '' }}</span></h3>
        @if (e.status === 'R') {
          <p class="aud-note">{{ 'audit.execution.logsAtEnd' | translate }}</p>
        }
        @if (d.logs.length > 8) {
          <input
            class="aud-input aud-log-search"
            type="search"
            [placeholder]="'audit.execution.searchLogs' | translate"
            [attr.aria-label]="'audit.execution.searchLogs' | translate"
            [value]="logQuery()"
            (input)="logQuery.set($any($event.target).value)" />
        }
        @if (d.logs.length === 0) {
          <p class="aud-muted">{{ 'audit.execution.noLogs' | translate }}</p>
        } @else {
          <ol class="aud-logs">
            @for (l of logs(); track l.seq) {
              <li>
                <time [title]="fmt().exact(l.date)">{{ fmt().dateTime(l.date) }}</time>
                <span class="aud-log-msg">{{ l.message }}</span>
                @if (l.number !== null) {
                  <span class="aud-muted">{{ fmt().number(l.number) }}</span>
                }
              </li>
            } @empty {
              <li class="aud-muted">{{ 'audit.filters.noMatch' | translate }}</li>
            }
          </ol>
          @if (d.logsTruncated) {
            <p class="aud-note">{{ 'audit.execution.logsTruncated' | translate }}</p>
          }
        }
      </section>
    }
  </div>
}
